import type { LanguageCode } from './languages'

export type QrLink = {
  id: string
  label: Record<LanguageCode, string>
  description?: Record<LanguageCode, string>
  href: Record<LanguageCode, string>
  enabled: boolean
  order: number
}

export type QrGroup = {
  id: string
  label: Record<LanguageCode, string>
  description: Record<LanguageCode, string>
  icon: 'info' | 'room-service' | 'spa' | 'laundry' | 'campaign' | 'sustainability' | 'menu'
  coverImage: string
  enabled: boolean
  order: number
  links: QrLink[]
}

const label = (tr: string, en: string, ru: string) => ({ tr, en, ru })
const localized = (slug: string) => ({
  tr: `/tr/${slug}`,
  en: `/en/${slug}`,
  ru: `/ru/${slug}`,
})

export const defaultQrGroups: QrGroup[] = [
  {
    id: 'breakfast',
    label: label('Kahvalti', 'Breakfast', 'Завтраки'),
    description: label(
      'Serpme kahvalti ve gunluk kahvalti tabaklari.',
      'Shared breakfast and daily breakfast plates.',
      'Завтраки для компании и ежедневные блюда.',
    ),
    icon: 'menu',
    coverImage: '/modi-cafe-logo.png',
    enabled: true,
    order: 10,
    links: [
      {
        id: 'breakfast-menu',
        label: label('Kahvalti Menusu', 'Breakfast Menu', 'Меню завтраков'),
        href: localized('kahvaltilar/'),
        enabled: true,
        order: 10,
      },
    ],
  },
  {
    id: 'hot-drinks',
    label: label('Sicak Icecekler', 'Hot Drinks', 'Горячие напитки'),
    description: label('Kahve, cay ve sicak icecek secimleri.', 'Coffee, tea and hot drink choices.', 'Кофе, чай и горячие напитки.'),
    icon: 'menu',
    coverImage: '/modi-cafe-logo.png',
    enabled: true,
    order: 20,
    links: [
      {
        id: 'hot-drinks-menu',
        label: label('Sicak Icecek Menusu', 'Hot Drinks Menu', 'Меню горячих напитков'),
        href: localized('sicak-icecekler/'),
        enabled: true,
        order: 10,
      },
    ],
  },
  {
    id: 'cold-drinks',
    label: label('Soguk Icecekler', 'Cold Drinks', 'Холодные напитки'),
    description: label('Ferah icecekler, soguk kahveler ve mevsimlik secimler.', 'Fresh drinks, iced coffees and seasonal choices.', 'Свежие напитки, айс-кофе и сезонные варианты.'),
    icon: 'menu',
    coverImage: '/modi-cafe-logo.png',
    enabled: true,
    order: 30,
    links: [
      {
        id: 'cold-drinks-menu',
        label: label('Soguk Icecek Menusu', 'Cold Drinks Menu', 'Меню холодных напитков'),
        href: localized('soguk-icecekler/'),
        enabled: true,
        order: 10,
      },
    ],
  },
  {
    id: 'desserts',
    label: label('Tatli & Pastane', 'Desserts', 'Десерты'),
    description: label('Gunluk tatlilar ve kahve yani lezzetleri.', 'Daily desserts and coffee pairings.', 'Десерты дня и пары к кофе.'),
    icon: 'menu',
    coverImage: '/modi-cafe-logo.png',
    enabled: true,
    order: 40,
    links: [
      {
        id: 'desserts-menu',
        label: label('Tatli Menusu', 'Dessert Menu', 'Меню десертов'),
        href: localized('tatlilar/'),
        enabled: true,
        order: 10,
      },
    ],
  },
  {
    id: 'campaigns',
    label: label('Kampanyalar', 'Campaigns', 'Акции'),
    description: label('Guncel cafe duyurulari ve donemsel lezzetler.', 'Current cafe updates and seasonal flavors.', 'Новости кафе и сезонные вкусы.'),
    icon: 'campaign',
    coverImage: '/modi-cafe-logo.png',
    enabled: true,
    order: 50,
    links: [
      {
        id: 'campaigns-page',
        label: label('Kampanyalar', 'Campaigns', 'Акции'),
        href: localized('kampanyalar/'),
        enabled: true,
        order: 10,
      },
    ],
  },
  {
    id: 'contact',
    label: label('Iletisim', 'Contact', 'Контакты'),
    description: label('Telefon, WhatsApp ve yol tarifi bilgileri.', 'Phone, WhatsApp and directions.', 'Телефон, WhatsApp и маршрут.'),
    icon: 'info',
    coverImage: '/modi-cafe-logo.png',
    enabled: true,
    order: 60,
    links: [
      {
        id: 'contact-page',
        label: label('Iletisim', 'Contact', 'Контакты'),
        href: localized('iletisim/'),
        enabled: true,
        order: 10,
      },
    ],
  },
]

const legacyQrGroupIds = new Set([
  'general',
  'info',
  'hotel-info',
  'room-service',
  'rooms',
  'laundry',
  'spa',
  'sustainability',
  'surdurulebilirlik',
  'menus',
  'restaurant',
])

const legacyLabelPattern =
  /black|bird|otel|hotel|oda|room|servis|service|spa|camasir|çamaşır|laundry|surdur|sürdürü|sustain|genel|general|bilgi|info/i

export const isModiCafeQrGroup = (group: QrGroup) => {
  if (legacyQrGroupIds.has(group.id)) return false
  if (group.icon === 'room-service' || group.icon === 'spa' || group.icon === 'laundry' || group.icon === 'sustainability') {
    return false
  }

  const labelText = Object.values(group.label).join(' ')
  const descriptionText = Object.values(group.description).join(' ')
  return !legacyLabelPattern.test(`${labelText} ${descriptionText}`)
}
